import { identity } from "deso-protocol";

import { focusAPIInstance } from "@/backend/focus/index";
import type { UserSocialProfiles } from "@/contexts/user-context";
import { getFocusAuthHeaders } from "@/utils/api-utils";
import { FocusDateParser } from "@/utils/date";

export enum USER_ROLES {
  none = "NONE",
  admin = "ADMIN",
  moderator = "MODERATOR",
  superAdmin = "SUPER_ADMIN",
}

export interface UserRolesResponse {
  DeletedAt: string;
  PublicKeyBase58Check: string;
  Role: USER_ROLES;
  UpdatedAt: string;
  UpdatedBy: string;
}

export async function getMyRoles(
  publicKeyBase58Check: string,
): Promise<UserRolesResponse> {
  const jwt = await identity.jwt();
  const response = await focusAPIInstance.get<UserRolesResponse>(
    `user/roles/me`,
    getFocusAuthHeaders(jwt, publicKeyBase58Check),
  );
  return response.data;
}

export async function getUserRoles(
  publicKeyBase58Check: string,
  params?: { user?: string; roles?: USER_ROLES[] },
): Promise<Array<UserRolesResponse>> {
  const jwt = await identity.jwt();

  const searchParams = new URLSearchParams();
  if (params?.user) {
    searchParams.append("user", params.user);
  }
  if (params?.roles) {
    searchParams.append("roles", params.roles.join(","));
  }

  const response = await focusAPIInstance.get<Array<UserRolesResponse>>(
    `user/roles?${searchParams.toString()}`,
    getFocusAuthHeaders(jwt, publicKeyBase58Check),
  );

  return response.data;
}

export async function createRole(
  publicKeyBase58Check: string,
  payload: Pick<UserRolesResponse, "PublicKeyBase58Check" | "Role">,
): Promise<UserRolesResponse> {
  const jwt = await identity.jwt();
  const response = await focusAPIInstance.post<UserRolesResponse>(
    `user/roles`,
    payload,
    getFocusAuthHeaders(jwt, publicKeyBase58Check),
  );
  return response.data;
}

export async function deleteAdministrator(
  publicKeyBase58Check: string,
  adminPublicKeyBase58Check: string,
): Promise<UserRolesResponse> {
  const jwt = await identity.jwt();
  const response = await focusAPIInstance.delete<UserRolesResponse>(
    `user/roles/${adminPublicKeyBase58Check}`,
    getFocusAuthHeaders(jwt, publicKeyBase58Check),
  );
  return response.data;
}

export interface AdminSettingsResponse {
  BountyHunterAlgoMaxJoiningRewardUsdCents: number;
  BountyHunterDisableRewardAutoApproval: boolean;
  BountyHunterRequireManualVerification: boolean;
  BountyHunterReferralRewardPercentageBasisPoints: number;
  BountyHunterRewardAutoApprovalLagHours: number;
  BountyHunterRewardMultiplierPercentageSchedule: Array<Array<number>>;
  BountyHunterRewardUnlockPercentage: number;
  ReferralCodeDefaultLength: number;
  ReferralCodeMinLength: number;
  ReferralCodeMaxLength: number;
  ReferralCodeMaxReferrals: number;
  TwitterBountyHunterAlgoFollowerCountMaxRewardUsdCents: number;
  TwitterBountyHunterAlgoIsVerifiedRewardUsdCents: number;
  TwitterBountyHunterAlgoJoiningRewardMinFollowers: number;
  TwitterBountyHunterAlgoJoiningRewardMinTweets: number;
  TwitterBountyHunterAlgoJoiningRewardMinViewsPerTweet: number;
  TwitterBountyHunterAlgoJoiningRewardNumTweetsToPull: number;
  TwitterBountyHunterAlgoJoiningRewardTweetPercentile: number;
  TwitterBountyHunterAlgoJoiningRewardUsdCentsSchedule: Array<Array<number>>;
  TwitterJoinToEarnTweetText: string;
  UpdatedAt: string;
  UpdatedBy: string;
  Version: number;
  ReferralUserFeeBasisPoints: number;
  FocusUserFeeBasisPoints: number;
  BountyHunterManualApprovalThresholdUsdCents: number;
  BountyHunterManualApprovalThresholdFocusNanosHex: string;
}

export async function getAdminSettings(
  publicKeyBase58Check: string,
): Promise<AdminSettingsResponse> {
  const jwt = await identity.jwt();

  const response = await focusAPIInstance.get<AdminSettingsResponse>(
    `settings/admin`,
    getFocusAuthHeaders(jwt, publicKeyBase58Check),
  );

  return response.data;
}

export async function updateAdminSettings(
  publicKeyBase58Check: string,
  payload: AdminSettingsResponse,
): Promise<AdminSettingsResponse> {
  const jwt = await identity.jwt();

  const response = await focusAPIInstance.post<AdminSettingsResponse>(
    `settings/admin`,
    {
      ...payload,
      UpdatedAt: FocusDateParser.now().toISOString(),
      UpdatedBy: publicKeyBase58Check,
    },
    getFocusAuthHeaders(jwt, publicKeyBase58Check),
  );

  return response.data;
}

export interface AdminSettingsUserOverrideResponse {
  AlgoTotalJoiningRewardUsdCents: number;
  IgnoreRewardMultiplier: boolean;
  ManualIsVerified: boolean;
  IsZeroManualReferralReward: boolean;
  ManualReferralRewardPercentageBasisPoints: number;
  ManualRewardUnlockPercentage: number;
  ManualTotalJoiningRewardUsdCents: number;
  TwitterUserId: string;
  UpdatedAt: string;
  UpdatedBy: string;
  ViewCount: number;
}

export async function getTwitterUserRewardsOverrides(
  publicKeyBase58Check: string,
  adminPublicKeyBase58Check: string,
): Promise<AdminSettingsUserOverrideResponse> {
  const jwt = await identity.jwt();

  const response =
    await focusAPIInstance.get<AdminSettingsUserOverrideResponse>(
      `twitter/user/rewards/overrides/${adminPublicKeyBase58Check}`,
      getFocusAuthHeaders(jwt, publicKeyBase58Check),
    );

  return response.data;
}

export interface AdminSettingsUserParamsResponse extends AdminSettingsUserOverrideResponse {
  AdjustedLockedJoiningRewardUsdCents: number;
  AdjustedLockedReferralRewardUsdCents: number;
  AdjustedTotalJoiningRewardUsdCents: number;
  AdjustedTotalReferralRewardUsdCents: number;
  AdjustedUnlockedJoiningRewardUsdCents: number;
  AdjustedUnlockedReferralRewardUsdCents: number;
  AlgoLockedJoiningRewardUsdCents: number;
  AlgoLockedReferralRewardUsdCents: number;
  AlgoReferralRewardPercentage: number;
  AlgoRewardUnlockPercentage: number;
  AlgoTotalReferralRewardUsdCents: number;
  AlgoUnlockedJoiningRewardUsdCents: number;
  AlgoUnlockedReferralRewardUsdCents: number;
  LockedJoiningRewardUsdCents: number;
  LockedReferralRewardUsdCents: number;
  ManualLockedJoiningRewardUsdCents: number;
  ManualLockedReferralRewardUsdCents: number;
  ManualTotalReferralRewardUsdCents: number;
  ManualUnlockedJoiningRewardUsdCents: number;
  ManualUnlockedReferralRewardUsdCents: number;
  ReferralRewardPercentage: number;
  RewardMultiplierPercentage: number;
  RewardUnlockPercentage: number;
  TotalJoiningRewardUsdCents: number;
  TotalReferralRewardUsdCents: number;
  UnlockedJoiningRewardUsdCents: number;
  UnlockedReferralRewardUsdCents: number;
  FocusPublicKeyBase58Check: string;
}

export async function getTwitterUserRewardsParams(
  publicKeyBase58Check: string,
  twitterUserName: string,
): Promise<AdminSettingsUserParamsResponse> {
  const jwt = await identity.jwt();

  const response = await focusAPIInstance.get<AdminSettingsUserParamsResponse>(
    `twitter/user/rewards/params/${twitterUserName}`,
    getFocusAuthHeaders(jwt, publicKeyBase58Check),
  );

  return response.data;
}

export async function updateTwitterUserRewards(
  publicKeyBase58Check: string,
  twitterUserName: string,
  payload: Partial<AdminSettingsUserOverrideResponse>,
): Promise<AdminSettingsUserOverrideResponse> {
  const jwt = await identity.jwt();

  const response =
    await focusAPIInstance.post<AdminSettingsUserOverrideResponse>(
      `twitter/user/rewards/overrides`,
      {
        ...payload,
        twitterUserName,
      },
      getFocusAuthHeaders(jwt, publicKeyBase58Check),
    );

  return response.data;
}

export enum REWARD_CLAIM_STATUS {
  PENDING = "PENDING",
  REJECTED = "REJECTED",
  APPROVED = "APPROVED",
  POST_DELETED = "POST_DELETED",
  PAYMENT_FINALIZED = "PAYMENT_FINALIZED",
  PAYMENT_REJECTED = "PAYMENT_REJECTED",
  PAYMENT_BROADCASTED = "PAYMENT_BROADCASTED",
  MANUAL_APPROVAL_REQUIRED = "MANUAL_APPROVAL_REQUIRED",
  PAYMENT_SKIPPED = "PAYMENT_SKIPPED",
  PAYMENT_TIMED_OUT = "PAYMENT_TIMED_OUT",
}

export interface RewardClaim {
  CreatedAt: string;
  JoiningPublicKeyBase58Check: string;
  LockedJoiningRewardTxnHash: string;
  LockedJoiningRewardUSDCents: number;
  TotalJoiningRewardFocusNanosHex: number | string;
  TotalReferralRewardFocusNanosHex: number | string;
  LockedReferralRewardTxnHash: string;
  LockedReferralRewardUSDCents: number;
  ReferralCode: string;
  ReferralPublicKeyBase58Check: string;
  RejectionReason: string;
  Status: REWARD_CLAIM_STATUS;
  TotalJoiningRewardUsdCents: number;
  TotalReferralRewardUsdCents: number;
  TwitterTweetId: string;
  UnlockedJoiningRewardTxnHash: string;
  UnlockedJoiningRewardUSDCents: number;
  UnlockedReferralRewardTxnHash: string;
  UnlockedReferralRewardUSDCents: number;
  UpdatedAt: string;
  UpdatedBy: string;
}
export interface RewardClaimsResponse {
  RewardClaims: Array<RewardClaim>;
  Count: number;
}

export async function getRewardClaims(
  publicKeyBase58Check: string,
  params: { [key: string]: string } = {},
) {
  const jwt = await identity.jwt();

  const response = await focusAPIInstance.get<RewardClaimsResponse>(
    `rewards/claims`,
    {
      ...getFocusAuthHeaders(jwt, publicKeyBase58Check),
      params,
    },
  );

  return response.data;
}

export async function updateRewardClaimStatus(
  publicKeyBase58Check: string,
  payload: {
    Status: REWARD_CLAIM_STATUS;
    RejectionReason?: string;
    PayLockedFocus: boolean;
  },
  claimerPublicKeyBase58Check: string,
) {
  const jwt = await identity.jwt();

  const response = await focusAPIInstance.post(
    `rewards/claims/${claimerPublicKeyBase58Check}`,
    {
      ...payload,
    },
    getFocusAuthHeaders(jwt, publicKeyBase58Check),
  );

  return response.data;
}

export type RewardClaimResponse = RewardClaim;

export enum CreateRewardClaimPlatforms {
  INSTAGRAM = "INSTAGRAM",
  TWITTER = "TWITTER",
}

type CreateRewardClaimsParams = {
  params: {
    platform?: CreateRewardClaimPlatforms;
  };
};

export async function createRewardClaim(
  publicKeyBase58Check: string,
  platform: CreateRewardClaimPlatforms,
  options: CreateRewardClaimsParams,
) {
  const jwt = await identity.jwt();
  const headers = getFocusAuthHeaders(jwt, publicKeyBase58Check);
  const response = await focusAPIInstance.post<RewardClaimResponse>(
    `rewards/claims`,
    {
      Platform: platform,
    },
    {
      ...options,
      ...headers,
    },
  );
  return response.data;
}

export interface TopEarnersResponse {
  EarningsUsdCents: number;
  PublicKeyBase58Check: string;
  Username: string;
  ExtraData: string;
  SocialProfiles: UserSocialProfiles;
}

export const DEFAULT_TOP_EARNERS_LIMIT = 3;

export async function getTopEarners(
  publicKeyBase58Check: string,
  category: string,
  payoutCategories: string,
  limit = DEFAULT_TOP_EARNERS_LIMIT,
) {
  const jwt = await identity.jwt();
  const response = await focusAPIInstance.get<{
    TopEarners: Array<TopEarnersResponse>;
  }>(`earnings/top-earners`, {
    ...getFocusAuthHeaders(jwt, publicKeyBase58Check),
    params: {
      category,
      payoutCategories,
      limit,
    },
  });
  return response.data?.TopEarners || [];
}

type EarningsByDay = {
  PublicKeyBase58Check: string;
  EarningsUsdCentsByDay: {
    [key: string]: number;
  };
};

export const getEarningsByDay = async ({
  afterDaysAgo,
  publicKey,
  currentUserPublicKey,
  subcategory,
}: {
  publicKey: string;
  afterDaysAgo?: number;
  subcategory?: string;
  currentUserPublicKey: string;
}) => {
  // TODO: @Mike please fix
  const jwt = await identity.jwt().catch(() => Promise.resolve(""));
  const response = await focusAPIInstance.get<EarningsByDay>(
    `earnings/by-day/${publicKey}`,
    {
      ...getFocusAuthHeaders(jwt, currentUserPublicKey),
      params: {
        afterDaysAgo,
        subcategory,
      },
    },
  );

  return response.data?.EarningsUsdCentsByDay || {};
};

type TwitterOauthAccessTokenResponse = {
  Success: boolean;
};

export async function deleteTwitterOauthAccessToken(
  publicKeyBase58Check: string,
  adminPublicKeyBase58Check: string,
): Promise<TwitterOauthAccessTokenResponse> {
  const jwt = await identity.jwt();
  const response =
    await focusAPIInstance.delete<TwitterOauthAccessTokenResponse>(
      `twitter/oauth/access-token/${publicKeyBase58Check}`,
      getFocusAuthHeaders(jwt, adminPublicKeyBase58Check),
    );
  return response.data;
}

export type AdminAuthorWeight = {
  CreatedAt: string;
  PublicKey: string;
  Reason: string;
  SetterPublicKey: string;
  UpdatedAt: string;
  Weight: number;
};

type AdminGetAuthorWeightsResponse = {
  authorWeights: AdminAuthorWeight[];
  total: number;
};

export async function getAuthorWeights(
  publicKeyBase58Check: string,
  limit: number,
  offset: number,
): Promise<AdminGetAuthorWeightsResponse> {
  const jwt = await identity.jwt();
  const response = await focusAPIInstance.get<AdminGetAuthorWeightsResponse>(
    `feed/admin/author/weight`,
    {
      ...getFocusAuthHeaders(jwt, publicKeyBase58Check),
      params: {
        limit,
        offset,
      },
    },
  );
  return response.data;
}

export interface PutAuthorWeightPayload {
  publicKey: string;
  weight: number;
  reason: string;
  setterPublicKey: string;
}

export interface PutAuthorWeightResponse {
  CreatedAt: string;
  PublicKey: string;
  Reason: string;
  SetterPublicKey: string;
  UpdatedAt: string;
  Weight: number;
}

export async function putAuthorWeight(
  publicKeyBase58Check: string,
  payload: PutAuthorWeightPayload,
): Promise<PutAuthorWeightResponse> {
  const jwt = await identity.jwt();
  const response = await focusAPIInstance.put<PutAuthorWeightResponse>(
    `feed/admin/author/weight`,
    payload,
    getFocusAuthHeaders(jwt, publicKeyBase58Check),
  );
  return response.data;
}

export interface DeleteAuthorWeightPayload {
  publicKey: string;
  setterPublicKey: string;
}

export async function deleteAuthorWeight(
  publicKeyBase58Check: string,
  payload: DeleteAuthorWeightPayload,
): Promise<void> {
  const jwt = await identity.jwt();
  await focusAPIInstance.delete(`feed/admin/author/weight`, {
    ...getFocusAuthHeaders(jwt, publicKeyBase58Check),
    data: payload,
  });
}

export interface GetSingleAuthorWeightResponse {
  CreatedAt: string;
  PublicKey: string;
  Reason: string;
  SetterPublicKey: string;
  UpdatedAt: string;
  Weight: number;
}

export async function getSingleAuthorWeight(
  publicKeyBase58Check: string,
  authorPublicKey: string,
): Promise<GetSingleAuthorWeightResponse> {
  const jwt = await identity.jwt();
  const response = await focusAPIInstance.get<GetSingleAuthorWeightResponse>(
    `feed/admin/author/weight/${authorPublicKey}`,
    getFocusAuthHeaders(jwt, publicKeyBase58Check),
  );
  return response.data;
}

export type AdminPostWeight = {
  CreatedAt: string;
  PostHash: string;
  Reason: string;
  SetterPublicKey: string;
  UpdatedAt: string;
  Weight: number;
};

export interface AdminGetPostWeightsResponse {
  postWeights: AdminPostWeight[];
  total: number;
}

export async function getPostWeights(
  publicKeyBase58Check: string,
  limit: number,
  offset: number,
): Promise<AdminGetPostWeightsResponse> {
  const jwt = await identity.jwt();
  const response = await focusAPIInstance.get<AdminGetPostWeightsResponse>(
    `feed/admin/post/weight`,
    {
      ...getFocusAuthHeaders(jwt, publicKeyBase58Check),
      params: {
        limit,
        offset,
      },
    },
  );
  return response.data;
}

export interface PutPostWeightPayload {
  postHash: string;
  weight: number;
  reason: string;
  setterPublicKey: string;
}

export interface PutPostWeightResponse {
  createdAt: string;
  postHash: string;
  reason: string;
  setterPublicKey: string;
  updatedAt: string;
  weight: number;
}

export async function putPostWeight(
  publicKeyBase58Check: string,
  payload: PutPostWeightPayload,
): Promise<PutPostWeightResponse> {
  const jwt = await identity.jwt();
  const response = await focusAPIInstance.put<PutPostWeightResponse>(
    `feed/admin/post/weight`,
    payload,
    getFocusAuthHeaders(jwt, publicKeyBase58Check),
  );
  return response.data;
}

export interface DeletePostWeightPayload {
  postHash: string;
  setterPublicKey: string;
}

export async function deletePostWeight(
  publicKeyBase58Check: string,
  payload: DeletePostWeightPayload,
): Promise<void> {
  const jwt = await identity.jwt();
  await focusAPIInstance.delete(`feed/admin/post/weight`, {
    ...getFocusAuthHeaders(jwt, publicKeyBase58Check),
    data: payload,
  });
}

export interface GetSinglePostWeightResponse {
  CreatedAt: string;
  PostHash: string;
  Reason: string;
  SetterPublicKey: string;
  UpdatedAt: string;
  Weight: number;
}

export async function getSinglePostWeight(
  publicKeyBase58Check: string,
  postHash: string,
): Promise<GetSinglePostWeightResponse> {
  const jwt = await identity.jwt();
  const response = await focusAPIInstance.get<GetSinglePostWeightResponse>(
    `feed/admin/post/weight/${postHash}`,
    getFocusAuthHeaders(jwt, publicKeyBase58Check),
  );
  return response.data;
}

export interface FeedSettingsResponse {
  blockInteractionDecayRate: number;
  blockWeight: number;
  finalPostScoreDecayRate: number;
  followCountWeight: number;
  followInteractionDecayRate: number;
  id: number;
  negativeReactionInteractionDecayRate: number;
  negativeReactionWeight: number;
  neutralReactionInteractionDecayRate: number;
  neutralReactionWeight: number;
  newPostDecayRate: number;
  newPostWeight: number;
  positiveReactionInteractionDecayRate: number;
  positiveReactionWeight: number;
  postMaxWealthContributionUsdCents: number;
  postScoreDecayRate: number;
  postScoreSigmoidScaling: number;
  quoteInteractionDecayRate: number;
  quoteWeight: number;
  replyInteractionDecayRate: number;
  replyWeight: number;
  reportInteractionDecayRate: number;
  reportWeight: number;
  repostInteractionDecayRate: number;
  repostWeight: number;
  showLessInteractionDecayRate: number;
  showLessWeight: number;
  tipInteractionDecayRate: number;
  tipWeight: number;
}

export async function getFeedSetting(
  publicKeyBase58Check: string,
): Promise<FeedSettingsResponse> {
  const jwt = await identity.jwt();
  const response = await focusAPIInstance.get<FeedSettingsResponse>(
    `feed/admin/settings`,
    getFocusAuthHeaders(jwt, publicKeyBase58Check),
  );
  return response.data;
}

export async function putFeedSettings(
  publicKeyBase58Check: string,
  settings: FeedSettingsResponse,
): Promise<FeedSettingsResponse> {
  const jwt = await identity.jwt();
  const response = await focusAPIInstance.put<FeedSettingsResponse>(
    `feed/admin/settings`,
    settings,
    getFocusAuthHeaders(jwt, publicKeyBase58Check),
  );
  return response.data;
}
